import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UnicornApi } from '@cld/unicorn-api';
import { User } from '@cld/application-wrapper-common';
import { fetchUserInfo } from './fetchUserInfo';

export const userDataContext = createContext<UserDataContextType | undefined>(undefined);

export const useUserData = () => {
  const context = useContext(userDataContext);

  if (!context) {
    throw new Error('You need to wrap your component with UserDataContext');
  }

  return context;
};

export const UserDataProvider: FC<{ children?: React.ReactNode; unicornApi?: UnicornApi }> = ({ children, unicornApi }) => {
  const [userData, setUserData] = useState<User | undefined>();

  const onError = useCallback((error: Error) => {
    console.error(error);
  }, []);

  const refreshUserData = useCallback(() => {
    unicornApi?.methods.notifyDataChanged('user');
    return fetchUserInfo()
      .then((response: User | undefined) => {
        setUserData(response);
        return response;
      })
      .catch(onError);
  }, [setUserData, onError]);

  // istanbul ignore next
  useEffect(() => {
    void refreshUserData();
  }, [refreshUserData]);

  const memoizedContextValue = useMemo(
    () => ({
      userData,
      updateUserData: setUserData,
      refreshUserData,
    }),
    [userData, setUserData, refreshUserData]
  );
  return <userDataContext.Provider value={memoizedContextValue}>{children}</userDataContext.Provider>;
};

export type UpdateUserData = (userData: User) => void;

export interface UserDataContextType {
  userData?: User;
  updateUserData: UpdateUserData;
  refreshUserData: () => Promise<void | User>;
}
